import { useCallback, useMemo, useState } from 'react';
import createContext from 'constate';
import useDeliveryFee from '../hooks/useDeliveryFee';
import useDeliveryMethod from '../hooks/useDeliveryMethod';
import { useLocation } from './LocationContext';
import { useCartStore } from './cart/CartStore';
import { shallow } from 'zustand/shallow';
import type { PaymentMethodDto } from '../../generated-api';

const [CheckoutContextProvider, useCheckout] = createContext(() => {
  const { isTakeAwayPreselected } = useLocation();
  const getDefaultVatType = useCallback(
    (): 'EAT_IN' | 'TAKEAWAY' => (isTakeAwayPreselected ? 'TAKEAWAY' : 'EAT_IN'),
    [isTakeAwayPreselected]
  );

  const { comment, vatType, saveComment, saveVatType } = useCartStore(
    (x) => ({
      comment: x.cart.comment,
      saveComment: x.saveComment,
      saveVatType: x.saveVatType,
      vatType: x.cart.vatType,
    }),
    shallow
  );

  const [tableNumber, setTableNumber] = useState<string>();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodDto>();
  const [dynamicFieldValues, setDynamicFieldValues] = useState<Record<string, string>>({});

  const [sidebarContent, setSidebarContent] = useState<
    'CART' | 'PAYMENT' | 'CREATE_TABLE' | 'SET_TABLE_NUMBER' | 'SELECT_TABLE'
  >('CART');

  const deliveryMethod = useDeliveryMethod(vatType, tableNumber);

  const resetVatType = useCallback(() => {
    saveVatType(getDefaultVatType());
  }, [getDefaultVatType, saveVatType]);

  const setToDefaultDeliveryMethod = useCallback(() => {
    resetVatType();
    resetTableNumber();
  }, [resetVatType]);

  const resetTableNumber = () => {
    setTableNumber(undefined);
  };

  const handleSetTableNumber = useCallback(
    (value: string) => {
      setTableNumber(value);
    },
    [setTableNumber]
  );

  const handleSetVatType = useCallback(
    (type: 'EAT_IN' | 'TAKEAWAY') => {
      saveVatType(type);
    },
    [saveVatType]
  );

  const deliveryFee = useDeliveryFee(deliveryMethod, tableNumber);

  return useMemo(
    () => ({
      comment,
      deliveryFee,
      deliveryMethod,
      dynamicFieldValues,
      resetTableNumber,
      selectedPaymentMethod,
      setComment: saveComment,
      setDynamicFieldValues,
      setSelectedPaymentMethod,
      setSidebarContent,
      setTableNumber: handleSetTableNumber,
      setToDefaultDeliveryMethod,
      setVatType: handleSetVatType,
      showCreateTableDialog: sidebarContent === 'CREATE_TABLE',
      showPaymentDialog: sidebarContent === 'PAYMENT',
      showSelectTable: sidebarContent === 'SELECT_TABLE',
      showSetTableNumber: sidebarContent === 'SET_TABLE_NUMBER',
      tableNumber,
      vatType,
    }),
    [
      comment,
      vatType,
      deliveryFee,
      deliveryMethod,
      dynamicFieldValues,
      handleSetTableNumber,
      handleSetVatType,
      saveComment,
      selectedPaymentMethod,
      setToDefaultDeliveryMethod,
      sidebarContent,
      tableNumber,
    ]
  );
});

export { CheckoutContextProvider, useCheckout };
